import type React from "react"
import { cn } from "../../lib/utils"

/**
 * One bubble/composer language for BOTH chat surfaces (ZPL-364), aligned to
 * the Figma chat design (MainLayout component; frame capture provided
 * 2026-07-14 — the audit's quota block had hidden it). The chat deliberately
 * speaks a SOFTER language than the architectural pages:
 * - Mareczek's bubbles are navy-filled, ~24px corners with the speaker-side
 *   tail corner tightened to ~8px (own bubbles mirror it);
 * - human bubbles (own + other) are light bordered cards — the inversion of
 *   the pre-design app; identity comes from side/avatar, not fill;
 * - the composer is a 12px rounded-rect (Figma inspect value) with a
 *   hairline border on the
 *   muted fill (not a pill);
 * - the send button is a chunky rounded square; its disabled state is the
 *   design's muted gray-blue token pair;
 * - failed bubbles flip to a light destructive tint so the marker reads on
 *   any kind.
 * The soft radii are design-sanctioned exceptions to the 2px page token.
 */

type ChatBubbleKind = "own" | "other" | "ai"

export function chatBubbleClass(
	kind: ChatBubbleKind,
	opts: { failed?: boolean } = {},
): string {
	return cn(
		// Frame measurement (MainLayout capture): three corners ~24px, the
		// corner pointing at the speaker tightened to ~8px.
		"min-w-0 max-w-[75%] rounded-[24px] leading-relaxed [overflow-wrap:anywhere]",
		kind === "own" ? "rounded-br-[8px]" : "rounded-bl-[8px]",
		kind === "ai" ? "px-4 py-3" : "px-4 py-2.5",
		kind === "ai"
			? "bg-primary text-primary-foreground"
			: "bg-card border border-border text-foreground",
		opts.failed &&
			"border border-destructive bg-destructive/10 text-foreground",
	)
}

/** Message-list backdrop shared by both surfaces. */
export const CHAT_SCROLL_AREA_CLASS = "bg-muted/40"

/** Composer textarea (callers add their own width class, e.g. w-full/flex-1). */
export const CHAT_COMPOSER_FIELD_CLASS =
	"resize-none rounded-[12px] border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 max-h-[200px]"

const SEND_BUTTON_CLASS =
	"h-12 w-12 shrink-0 rounded-[16px] bg-primary text-primary-foreground flex items-center justify-center cursor-pointer transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:bg-primary-disabled disabled:text-primary-disabled-foreground"

const SendIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m22 2-7 20-4-9-9-4Z" />
		<path d="M22 2 11 13" />
	</svg>
)

export function ChatSendButton({
	disabled,
	label,
}: {
	disabled: boolean
	label: string
}) {
	return (
		<button
			type="submit"
			disabled={disabled}
			aria-label={label}
			className={SEND_BUTTON_CLASS}
		>
			<SendIcon size={16} />
		</button>
	)
}
